import { useRef, useState, type PointerEvent } from "react";

interface Offset {
  dx: number;
  dy: number;
}

/**
 * Dra med mus eller berøring (pointer events). Gir forskyvningen mens man
 * drar, og kaller `onEnd` med endelig forskyvning når man slipper. Små
 * bevegelser under 3 px regnes som klikk og gir ingen `onEnd`.
 */
export function usePointerDrag(onEnd: (offset: Offset) => void, disabled = false) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const [offset, setOffset] = useState<Offset | null>(null);

  const handlers = {
    onPointerDown(e: PointerEvent<HTMLElement>) {
      if (disabled || e.button !== 0) return;
      start.current = { x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onPointerMove(e: PointerEvent<HTMLElement>) {
      if (!start.current) return;
      setOffset({ dx: e.clientX - start.current.x, dy: e.clientY - start.current.y });
    },
    onPointerUp(e: PointerEvent<HTMLElement>) {
      if (!start.current) return;
      const final = { dx: e.clientX - start.current.x, dy: e.clientY - start.current.y };
      start.current = null;
      setOffset(null);
      if (Math.abs(final.dx) >= 3 || Math.abs(final.dy) >= 3) onEnd(final);
    },
    onPointerCancel() {
      start.current = null;
      setOffset(null);
    },
  };

  return { offset, handlers };
}
